/**
 * Validation-message localization by stable issue `code` (Phase 4.1).
 *
 * The backend keeps each issue's `code` stable and English-anchored; the UI maps the code
 * to a localized template under `validation.codes.<code>` and interpolates field/value when
 * useful. When no translation exists for a code, we fall back to the server-provided
 * plain-English `message` so nothing renders blank or as a raw key.
 */
import type { TFunction } from "i18next";
import type { Issue, SpecIssue } from "../api/types";

type AnyIssue =
  | Pick<Issue, "code" | "message">
  | Pick<SpecIssue, "code" | "message" | "field" | "value">;

function fieldOf(issue: AnyIssue): string | undefined {
  if ("field" in issue && issue.field) return issue.field;
  // Config `Issue` carries the heading in `path` sometimes; SpecIssue uses `field`.
  return undefined;
}

/**
 * Localize a validation issue by its code. `t` is react-i18next's translate fn.
 * Returns the server `message` verbatim when the code has no `validation.codes.*` entry.
 */
export function localizeIssue(t: TFunction, issue: AnyIssue): string {
  const key = `validation.codes.${issue.code}`;
  if (!t(key, { defaultValue: "" })) {
    return issue.message;
  }
  const field = fieldOf(issue);
  const value = "value" in issue && issue.value != null ? String(issue.value) : "";
  return t(key, { field: field ?? "", value });
}
